import { useInitialValue, useInitialValues } from '@tanium/react-use-initial-value';
import { createUseHistoryState } from './createUseHistoryState';

/**
 * A hook that can dynamically construct and return a `useHistoryState` hook for
 * a given `stateKey`. If no state key is provided, returns `undefined`.
 *
 * The returned value is guaranteed to remain stable for the lifetime of the
 * component, and is based on the provided `stateKey` from the initial render.
 * Changing the `stateKey` after the initial render is not supported and may
 * result in unexpected behavior.
 *
 * This hook is intended to be used in cases where `stateKey` is only known at
 * runtime. If `stateKey` is known statically, use `createUseHistoryState`
 * instead.
 *
 * @param stateKey A globally unique key to identify the state associated with
 * the returned hook. If this parameter is undefined or empty, this function
 * will return `undefined` instead.
 *
 * @returns A `useHistoryState` hook, if a non-empty `stateKey` is provided, or
 * `undefined` otherwise.
 */
export const useCreateDynamicHistoryState = (stateKey?: string | undefined) => {
  const { stateKey: stableStateKey } = useInitialValues({
    stateKey,
  });

  return useInitialValue(() => {
    if (stableStateKey) {
      return createUseHistoryState(stableStateKey);
    }

    return undefined;
  });
};
